import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";

import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { User } from "../../../../api/graphql";
import { SiteUserListStore } from "../../store";
import { roleDisplayId } from "../../../role/store/RoleCommonStore";

import {
  VisibilityIcon,
  EditIcon,
  DeleteForeverIcon,
  ContentCopyIcon,
  SyncLockIcon,
} from "../../../../icons";

export type GridExpressProps = { listStore?: SiteUserListStore };

export const SiteUserListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<User>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: User) =>
            rootStore.router.goTo("/site-user/view/" + row.id),
          icon: <VisibilityIcon fontSize="small" />,
        },
        {
          label: langStore.editCommand,
          onClick: (row: User) =>
            rootStore.router.goTo(
              "/site-user/update/" + row.id,
              undefined,
              undefined,
              undefined,
              store.breadcrumbLabel
            ),
          icon: <EditIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("update"),
        },
        {
          label: langStore.deleteCommand,
          onClick: (row: User) => store.deleteRow(row.id),
          icon: <DeleteForeverIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("delete"),
        },
        {
          label: langStore.duplicateCommand,
          onClick: (row: User) =>
            rootStore.router.goTo(
              "/site-user/create/" + row.id,
              undefined,
              undefined,
              undefined,
              store.breadcrumbLabel
            ),
          icon: <ContentCopyIcon fontSize="small" />,
          isEnabled: () => store.isActionEnabled("create"),
        },
        {
          label: langStore.resetPasswordCommand,
          onClick: (row: User) => store.setResetPasswordEmail(row.email),
          icon: <SyncLockIcon fontSize="small" />,
          isVisible: (row: User) => row.active === true,
        },
      ],
      [langStore, store, rootStore.router]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<User>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "role") {
          return {
            ...col,
            id: "role",
            accessorFn: (row: User) =>
              row.role ? roleDisplayId(row.role, langStore) : "-",
          };
        }
        if (col.accessorKey === "language") {
          return {
            ...col,
            id: "language",
            accessorFn: (row: User) => {
              const v = (row as any).language;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.LanguageOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "idCardType") {
          return {
            ...col,
            id: "idCardType",
            accessorFn: (row: User) => {
              const v = (row as any).idCardType;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.IdCardTypeOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        if (col.accessorKey === "gender") {
          return {
            ...col,
            id: "gender",
            accessorFn: (row: User) => {
              const v = (row as any).gender;
              if (v === null || v === undefined || v === "") return "-";
              const opts = langStore.GenderOptions;
              const toLabel = (x: any) => {
                const o = opts.find((opt: any) => opt.id === x);
                return o ? o.displayId : x;
              };
              return Array.isArray(v)
                ? v.length
                  ? v.map(toLabel).join("\n")
                  : "-"
                : toLabel(v);
            },
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns, langStore]);

    const rows = store.userCurrentPage || [];

    return (
      <div id={"ListOfUser"}>
        <div id={"StartOfListingOfUser"} />
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
        />
        <div id={"EndOfListing"} />
      </div>
    );
  }
);
